// A dock: one floating button that opens a tabbed panel. The page runs two of them, stacked above the chat widget's own
// bubble: the Guide (the tour) and the Plant (Controls / Events / Alarms / Trends); kraftware-dev#120. Docks that share a
// `group` take turns: opening one closes the other, and the page layout follows whichever is open.
// Desktop: a panel docked to the right edge. Phone (<= 640 px): a bottom sheet with three heights (bar / half / full).
// Self-contained: it builds its own chrome and adopts any `[data-dock-tab="<tab>"]` children of `root` into the matching
// pane (moved, not cloned, so their ids keep working). Later tasks fill the empty panes through `dock.el(tab)`.

export const PHONE_QUERY = "(max-width: 640px)"
const LABELS = { guide: "Guide", controls: "Controls", chat: "Chat", events: "Events", alarms: "Alarms", trends: "Trends" }
const CONTACT = "eric.kraft@kraftware.dev"
const FOCUSABLE = "button, [href], select, input, textarea, [tabindex]"
const SVG_OPEN = `<svg class="dock-ico dock-ico-open" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M15.8 8.2l-2.2 5.4-5.4 2.2 2.2-5.4z" fill="currentColor"/></svg>`
/** The Plant dock's button: three sliders. */
export const SVG_PLANT = `<svg class="dock-ico dock-ico-open" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false"><path d="M5 4v16M12 4v16M19 4v16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><rect x="2.5" y="13" width="5" height="3.5" rx="1.2" fill="currentColor"/><rect x="9.5" y="6.5" width="5" height="3.5" rx="1.2" fill="currentColor"/><rect x="16.5" y="11" width="5" height="3.5" rx="1.2" fill="currentColor"/></svg>`
/** The Guide button as a resume control: ▶ and the chapter it leads back to (setFab). */
const SVG_PLAY = `<svg class="dock-ico dock-ico-play" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M7 4.5v15l12.5-7.5z" fill="currentColor"/></svg>`
const SVG_CLOSE = `<svg class="dock-ico dock-ico-close" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>`

/** The visitor context the chat agent receives: where the visitor is in the tour and what is alarming right now. */
export function chatPayload({ chapterTitle = "", alarms = [], events = [] } = {}) {
  const where = chapterTitle ? `chapter "${chapterTitle}"` : "the free-explore view"
  const al = alarms.length ? `Active alarms: ${alarms.join("; ")}.` : "No active alarms."
  // The detector's own words for what just happened (its interpretations), newest last.
  const ev = events.length ? ` Recently detected: ${events.map((x) => String(x).replace(/\.$/, "")).join("; ")}.` : ""
  return {
    visitorContext: `Exploring the interactive six-station batching and holding tank lifecycle simulator on kraftware.dev/demos/batch-lifecycle, at ${where}. ${al}${ev}`,
    customGreeting: `[KraftWare AI] I see you're at ${chapterTitle ? `"${chapterTitle}"` : "the simulator"}${alarms.length ? ` and ${alarms[0]} is active` : ""}. What would you like to dig into — dosing accuracy, valve wear, the shock envelope, or how this maps onto your equipment?`,
  }
}

/** Docks that take turns (see the header). Pass the same group to each createDock. */
export function createDockGroup() { return { members: [] } }

export function createDock(root, opts = {}) {
  const doc = root.ownerDocument, win = doc.defaultView
  const tabs = opts.tabs || ["guide", "controls", "chat", "events"]
  // Element ids: the first dock keeps the original ones (dockFab, dockPanel, dockBadge); another is prefixed by its name.
  const name = opts.name || "dock"
  const L = Object.assign({ open: "Open the guide: tour, controls, chat and events", close: "Close the guide", panel: "Guide dock", tabs: "Guide dock sections" }, opts.labels || {})
  const group = opts.group || null
  const chatTimeoutMs = opts.chatTimeoutMs ?? 6000
  let chatContext = opts.chatContext || (() => ({}))
  const tabSubs = opts.onTab ? [opts.onTab] : []
  const openSubs = []
  const userSubs = []   // the visitor's own opens and closes (onUser), as opposed to the page's api calls
  const emitUser = (kind) => { for (const fn of userSubs) fn(kind) }
  let pill = null       // setFab({ label, title }): the closed button reads "▶ <label>"
  let active = tabs[0]
  let state = "closed"          // desktop: closed | open ; phone: closed | bar | half | full

  const mql = win.matchMedia ? win.matchMedia(PHONE_QUERY) : { matches: false }
  let phone = !!mql.matches

  // --- adopt host content before rebuilding root
  const adopt = {}
  for (const c of Array.from(root.children)) if (c.dataset && c.dataset.dockTab) adopt[c.dataset.dockTab] = c
  root.textContent = ""
  root.classList.add("dock")

  const h = (tag, attrs = {}, html) => { const e = doc.createElement(tag); for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v); if (html != null) e.innerHTML = html; return e }
  const fab = h("button", { id: `${name}Fab`, class: "dock-fab", type: "button", "aria-expanded": "false", "aria-controls": `${name}Panel`, "aria-label": L.open, title: L.open }, (opts.icon || SVG_OPEN) + SVG_PLAY + SVG_CLOSE)
  const fabLabelEl = h("span", { class: "dock-fab-label" }); fabLabelEl.hidden = true
  fab.appendChild(fabLabelEl)
  const badgeEl = h("span", { id: `${name}Badge`, class: "dock-badge", "aria-hidden": "true" }); badgeEl.hidden = true
  fab.appendChild(badgeEl)
  const bar = h("div", { class: "dock-bar" }); bar.hidden = true
  const barMain = h("button", { class: "dock-bar-main", type: "button", "aria-label": "Open the guide" }, `<span class="dock-bar-badge" hidden></span><span class="dock-bar-title">Guide</span>`)
  const barActions = h("span", { class: "dock-bar-actions" })
  bar.append(barMain, barActions)
  const panel = h("section", { id: `${name}Panel`, class: "dock-panel", role: "dialog", "aria-label": L.panel }); panel.hidden = true
  const grip = h("div", { class: "dock-grip", role: "separator", "aria-orientation": "vertical", "aria-label": "Resize panel", tabindex: "0" })
  const handle = h("button", { class: "dock-handle", type: "button", "aria-label": "Resize panel: half or full height" }, `<span aria-hidden="true"></span>`)
  const tablist = h("div", { class: "dock-tabs", role: "tablist", "aria-label": L.tabs })
  const panes = h("div", { class: "dock-panes" })
  const tabEls = {}, paneEls = {}
  for (const t of tabs) {
    const b = h("button", { class: "dock-tab", type: "button", role: "tab", id: `dockTab-${t}`, "data-tab": t, "aria-controls": `dockPane-${t}`, "aria-selected": "false", tabindex: "-1" }, LABELS[t] || t)
    const p = h("div", { class: `dock-pane dock-pane-${t}`, id: `dockPane-${t}`, role: "tabpanel", "aria-labelledby": `dockTab-${t}`, "data-pane": t }); p.hidden = true
    if (adopt[t]) { adopt[t].removeAttribute("data-dock-tab"); adopt[t].classList.add("dock-slot"); p.appendChild(adopt[t]) }
    tabEls[t] = b; paneEls[t] = p; tablist.appendChild(b); panes.appendChild(p)
  }
  tablist.hidden = tabs.length === 1   // a single-tab dock (the Guide) needs no tab row
  panel.append(grip, handle, tablist, panes)
  root.append(fab, bar, panel)

  // --- Chat tab
  const chatBtn = h("button", { id: "dockChatBtn", class: "btn btn-primary dock-chat-btn", type: "button" }, "connecting…"); chatBtn.disabled = true
  const fallback = h("p", { class: "dock-chat-fallback" }, `The chat assistant is not reachable right now. You can email <a href="mailto:${CONTACT}">${CONTACT}</a> instead.`); fallback.hidden = true
  if (paneEls.chat) {
    paneEls.chat.append(
      h("p", { class: "dock-chat-intro" }, "Ask the engineering agent about dosing accuracy, valve wear, the mixer shock envelope, or how this maps onto your equipment. It knows which chapter you are on and what is alarming."),
      chatBtn, fallback)
  }
  const chatReady = () => typeof win.xlcsOpen === "function"
  let chatTimer = null, chatDeadline = null
  function syncChat() {
    if (chatReady()) {
      chatBtn.disabled = false; chatBtn.textContent = "Ask the engineering agent"; fallback.hidden = true
      if (chatTimer) { win.clearInterval(chatTimer); chatTimer = null }
      if (chatDeadline) { win.clearTimeout(chatDeadline); chatDeadline = null }
    }
  }
  function chatGaveUp() { if (!chatReady()) { fallback.hidden = false; chatBtn.disabled = true; chatBtn.textContent = "Chat unavailable" } }
  if (chatReady()) syncChat()
  else if (paneEls.chat) {
    chatTimer = win.setInterval(syncChat, 500)
    if (chatTimeoutMs <= 0) chatGaveUp(); else chatDeadline = win.setTimeout(chatGaveUp, chatTimeoutMs)
  }
  chatBtn.addEventListener("click", () => {
    if (!chatReady()) return
    const p = chatPayload(chatContext() || {})
    api.close()                       // the widget's own box takes this corner; close first so its input keeps focus
    win.xlcsOpen({ visitorContext: p.visitorContext, customGreeting: p.customGreeting })
  })

  // --- state
  const isOpen = () => state === "open" || state === "half" || state === "full"
  const headerEl = () => doc.querySelector(".scada-header")
  function measure() { const hd = headerEl(); if (hd) root.style.setProperty("--dock-top", `${hd.offsetHeight}px`) }

  function render() {
    root.dataset.sheet = state
    root.classList.toggle("dock-phone", phone)
    root.classList.toggle("dock-open", isOpen())
    root.classList.toggle("dock-bar-on", state === "bar")
    root.classList.toggle("dock-ready", true)
    // legacy-friendly alias used by tests and styles
    root.classList.toggle("dock-bar", state === "bar")
    panel.hidden = !isOpen()
    bar.hidden = !(phone && state === "bar")
    fab.hidden = false
    fab.setAttribute("aria-expanded", String(isOpen()))
    fab.setAttribute("aria-label", fabLabel()); fab.title = fabLabel()
    for (const t of tabs) {
      const sel = t === active
      tabEls[t].setAttribute("aria-selected", String(sel)); tabEls[t].tabIndex = sel ? 0 : -1
      paneEls[t].hidden = !sel
    }
    syncBody()
  }
  const fabLabel = () => (isOpen() ? L.close : (pill ? pill.title || pill.label : L.open) + (badgeN ? ` (${badgeN} active)` : ""))
  /** The page layout follows whichever dock of the group is open (or this one alone, without a group). */
  function syncBody() {
    const members = group ? group.members : [self]
    const b = doc.body
    b.classList.toggle("dock-panel-open", members.some((m) => m.isOpen() && !m.isPhone()))
    for (const s of ["bar", "half", "full"]) b.classList.toggle(`dock-sheet-${s}`, members.some((m) => m.isPhone() && m.sheetState() === s))
    for (const m of members) b.classList.toggle(`dock-open-${m.name}`, m.isOpen())
  }
  let badgeN = 0
  function setState(next) {
    const was = isOpen()
    state = next; render()
    if (was !== isOpen()) for (const fn of openSubs) fn(isOpen())
  }
  function setActive(t, silent) {
    if (!tabs.includes(t) || (t === active && !silent)) { return }
    active = t; render()
    if (!silent) for (const fn of tabSubs) fn(t)
  }

  const self = { name, isOpen: () => isOpen(), isPhone: () => phone, sheetState: () => state, root }
  const api = {
    name,
    open(tab) {
      // Docks in a group take turns: close the others first, so the page layout ends on this one.
      if (group) for (const m of group.members) if (m !== self && m.sheetState() !== "closed") m.api.close()   // a phone bar too
      if (tab) setActive(tab)
      else if (tabSubs.length && state === "closed") for (const fn of tabSubs) fn(active)   // opening on the default tab still reports it
      measure()
      setState(phone ? (state === "full" ? "full" : "half") : "open")
      tabEls[active].focus()
    },
    close() {
      const inside = panel.contains(doc.activeElement) || bar.contains(doc.activeElement)
      setState("closed")
      if (inside) fab.focus()
    },
    collapse() { if (phone) setState("bar"); else api.close() },
    sheet(height) { if (phone && ["half", "full", "bar"].includes(height)) setState(height) },
    isOpen,
    el(t) { return t === "bar" ? barActions : paneEls[t] },
    onOpenChange(fn) { openSubs.push(fn) },
    onTab(fn) { tabSubs.push(fn) },
    badge(n) {
      badgeN = Math.max(0, Math.floor(Number(n) || 0))
      badgeEl.hidden = badgeN === 0; badgeEl.textContent = badgeN > 99 ? "99+" : String(badgeN)
      const bb = bar.querySelector(".dock-bar-badge"); bb.hidden = badgeN === 0; bb.textContent = badgeEl.textContent   // the FAB is hidden while the bar shows
      fab.setAttribute("aria-label", fabLabel())
    },
    setBar({ title } = {}) { if (title != null) bar.querySelector(".dock-bar-title").textContent = title },
    /**
     * The closed button as a labelled pill, "▶ <label>" (the Guide: the chapter the tour carries on at), or back to its
     * round icon with null. While the panel is open the button is its round close button either way.
     */
    setFab(spec) {
      pill = spec && spec.label ? { label: String(spec.label), title: spec.title ? String(spec.title) : "" } : null
      fab.classList.toggle("dock-fab-pill", !!pill)
      fabLabelEl.hidden = !pill; fabLabelEl.textContent = pill ? pill.label : ""
      fab.setAttribute("aria-label", fabLabel()); fab.title = fabLabel()
    },
    /** The visitor opened ("open"), closed ("close") or, on a phone, collapsed ("collapse") this dock themselves. */
    onUser(fn) { userSubs.push(fn) },
    setChatContext(fn) { chatContext = fn },
    activeTab: () => active,
    destroy() {
      if (chatTimer) win.clearInterval(chatTimer); if (chatDeadline) win.clearTimeout(chatDeadline)
      doc.removeEventListener("keydown", onKey, true); doc.removeEventListener("pointerdown", onOutside, true)
      if (mql.removeEventListener) mql.removeEventListener("change", onMedia)
      if (ro) ro.disconnect()
      if (group) group.members.splice(group.members.indexOf(self), 1)
      doc.body.classList.remove(`dock-open-${name}`)
      if (!group || !group.members.length) doc.body.classList.remove("dock-panel-open", "dock-sheet-bar", "dock-sheet-half", "dock-sheet-full")
      else syncBody()
    },
  }
  self.api = api
  if (group) group.members.push(self)

  // --- events
  fab.addEventListener("click", () => { if (isOpen()) { api.close(); emitUser("close") } else { api.open(); emitUser("open") } })
  barMain.addEventListener("click", () => { api.open(); emitUser("open") })
  for (const t of tabs) tabEls[t].addEventListener("click", () => setActive(t))
  tablist.addEventListener("keydown", (e) => {
    const i = tabs.indexOf(active); let n = -1
    if (e.key === "ArrowRight") n = (i + 1) % tabs.length; else if (e.key === "ArrowLeft") n = (i - 1 + tabs.length) % tabs.length
    else if (e.key === "Home") n = 0; else if (e.key === "End") n = tabs.length - 1
    if (n < 0) return
    e.preventDefault(); setActive(tabs[n]); tabEls[tabs[n]].focus()
  })

  function focusables() {
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled && !el.closest("[hidden]") && el.tabIndex >= 0 && !(el.getAttribute("tabindex") === "-1"))
  }
  // A visible modal outside the dock (welcome overlay, Operator pane) owns Tab and Escape.
  const modalOpen = () => Array.from(doc.querySelectorAll('[aria-modal="true"]')).some((m) => !root.contains(m) && !m.hidden && !m.closest("[hidden]") && (!m.classList.contains("fp-backdrop") || m.classList.contains("open")))
  function onKey(e) {
    if (!isOpen() || modalOpen()) return
    if (e.key === "Escape") { e.preventDefault(); api.close(); fab.focus(); emitUser("close"); return }
    if (e.key !== "Tab") return
    const f = focusables(); if (!f.length) return
    const first = f[0], last = f[f.length - 1], cur = doc.activeElement
    if (!root.contains(cur)) return   // trap only from inside the dock; never pull focus in from the page
    if (e.shiftKey && cur === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && cur === last) { e.preventDefault(); first.focus() }
  }
  doc.addEventListener("keydown", onKey, true)

  // A tap on the stage collapses the phone sheet to its bar; on desktop the panel stays (the trend remains usable beside it).
  // Not a stage tap: the dock itself, the lens (it floats above the sheet) and the alarm banner (its ACK).
  const KEEP_OPEN = ".lens, #alarmBanner, #xlcs-box, #xlcs-btn, .chat-cue"
  function onOutside(e) {
    if (!phone || !(state === "half" || state === "full")) return
    const t = e.target
    if (root.contains(t) || (t && t.closest && t.closest(KEEP_OPEN))) return
    if (group && group.members.some((m) => m.root.contains(t))) return
    setState("bar")
    emitUser("collapse")
  }
  doc.addEventListener("pointerdown", onOutside, true)

  function onMedia(e) {
    phone = !!e.matches
    if (phone) setState(state === "open" ? "half" : state === "closed" ? "closed" : state)
    else setState(state === "bar" ? "closed" : state === "half" || state === "full" ? "open" : state)
    render()
  }
  if (mql.addEventListener) mql.addEventListener("change", onMedia)

  // Handle: a click cycles half <-> full; a drag snaps to the nearest of bar / half / full.
  let drag = null, swallow = false
  handle.addEventListener("click", () => { if (swallow) { swallow = false; return } if (phone) setState(state === "full" ? "half" : "full") })
  handle.addEventListener("pointerdown", (e) => { if (!phone) return; drag = { y0: e.clientY, h0: panel.getBoundingClientRect().height, moved: false }; try { handle.setPointerCapture(e.pointerId) } catch (_) { /* no capture in this environment */ } })
  handle.addEventListener("pointermove", (e) => {
    if (!drag) return
    const dy = e.clientY - drag.y0; if (Math.abs(dy) > 6) drag.moved = true
    if (drag.moved) panel.style.height = `${Math.max(56, Math.min(win.innerHeight - (parseFloat(root.style.getPropertyValue("--dock-top")) || 0), drag.h0 - dy))}px`
  })
  const endDrag = () => {
    if (!drag) return
    const d = drag; drag = null
    if (!d.moved) return
    swallow = true
    const hgt = parseFloat(panel.style.height) || d.h0, vh = win.innerHeight
    panel.style.height = ""
    const to = [["bar", 56], ["half", vh * 0.55], ["full", vh - (parseFloat(root.style.getPropertyValue("--dock-top")) || 0)]].sort((a, b) => Math.abs(a[1] - hgt) - Math.abs(b[1] - hgt))[0][0]
    setState(to)
  }
  handle.addEventListener("pointerup", endDrag); handle.addEventListener("pointercancel", endDrag)

  // Grip: desktop width 380..480 px, by drag or arrow keys.
  const setW = (w) => {   // set on <html> too: the stage margin and header padding are siblings of the dock, not descendants
    const v = `${Math.max(380, Math.min(480, Math.round(w)))}px`
    root.style.setProperty("--dock-w", v); doc.documentElement.style.setProperty("--dock-w", v)
  }
  let gripDrag = false
  grip.addEventListener("pointerdown", (e) => { gripDrag = true; try { grip.setPointerCapture(e.pointerId) } catch (_) { /* ok */ } })
  grip.addEventListener("pointermove", (e) => { if (gripDrag) setW(win.innerWidth - e.clientX) })
  grip.addEventListener("pointerup", () => { gripDrag = false })
  grip.addEventListener("keydown", (e) => {
    const cur = parseFloat(root.style.getPropertyValue("--dock-w")) || 380
    if (e.key === "ArrowLeft") { e.preventDefault(); setW(cur + 20) } else if (e.key === "ArrowRight") { e.preventDefault(); setW(cur - 20) }
  })

  let ro = null
  const hd = headerEl()
  if (hd && typeof win.ResizeObserver === "function") { ro = new win.ResizeObserver(measure); ro.observe(hd) }
  measure()
  setActive(active, true)
  render()
  return api
}
